import { Info, Sparkle } from '@phosphor-icons/react';
import React, { useState } from 'react';
import { Select, PriceBreakupAccordion, PriceLineItem } from 'app/components';
import './style';
import { useSelector } from 'react-redux';
import { getValueByPath } from '../../../../utils/commonUtils';

const GST_TICKET_BREAKUP = [
  { id: 'Organizer.GST.basePrice', label: 'Base Price (excl. GST)' },
  { id: 'Organizer.GST.gst', label: 'GST 18%' },
  { id: 'Organizer.GST.ticketPrice', label: 'Ticket Price' },
  {
    id: 'Buyer.GST.total_booking_fee',
    label: 'Booking Fees (incl. GST)',
    sub_lineitems: [
      { id: 'Buyer.GST.base_price', label: 'Base Price' },
      { id: 'Buyer.GST.taxes', label: 'Taxes @ 18%' },
    ],
  },
  { id: 'Buyer.GST.final_amount', label: 'Amount paid by buyer', bold: true },
];

const NONGST_TICKET_BREAKUP = [
  { id: 'Organizer.NON-GST.basePrice', label: 'Base Price' },
  { id: 'Organizer.NON-GST.ticketPrice', label: 'Ticket Price' },
  {
    id: 'Buyer.NON-GST.total_booking_fee',
    label: 'Booking Fees (incl. GST)',
    sub_lineitems: [
      { id: 'Buyer.NON-GST.base_price', label: 'Base Price' },
      { id: 'Buyer.NON-GST.taxes', label: 'Taxes @ 18%' },
    ],
  },
  { id: 'Buyer.NON-GST.final_amount', label: 'Amount paid by buyer', bold: true },
];

const GST_ORGANIZER_BREAKUP = [
  {
    id: 'Organizer.GST.totalCommision',
    label: 'Platform Fee (incl. GST)',
    sub_lineitems: [
      { id: 'Organizer.GST.commission', label: 'Base Price (incl. PG Fee)' },
      { id: 'Organizer.GST.gst_on_commission', label: 'Taxes @ 18%' },
    ],
  },
  { id: 'Organizer.GST.tds', label: 'TDS 0.1%' },
  { id: 'Organizer.GST.tcs', label: 'TCS 1%' },
];

const NONGST_ORGANIZER_BREAKUP = [
  {
    id: 'Organizer.NON-GST.totalCommision',
    label: 'Platform Fee (incl. GST)',
    sub_lineitems: [
      { id: 'Organizer.NON-GST.commission', label: 'Base Price (incl. PG Fee)' },
      { id: 'Organizer.NON-GST.gst_on_commission', label: 'Taxes @ 18%' },
    ],
  },
  { id: 'Organizer.NON-GST.tds', label: 'TDS 0.1%' },
  { id: 'Organizer.NON-GST.tcs', label: 'TCS 1%' },
];

const Breakup = ({ currentBreakup, config, isLoading }) => (
  config?.map((lineitem) => (
    lineitem.sub_lineitems?.length ? (
      <PriceBreakupAccordion
        title={lineitem.label}
        price={getValueByPath(currentBreakup, lineitem.id)}
        width="100%"
        isLoading={isLoading}
        content={(
          <div className='flex flex-col gap-3'>
            {lineitem.sub_lineitems.map((subitem) => (
              <PriceLineItem label={subitem.label} price={getValueByPath(currentBreakup, subitem.id)} priceClass="label-14" labelClass="text-14" isLoading={isLoading} />
            ))}
          </div>
        )}
      />) : (
      <PriceLineItem label={lineitem.label} price={getValueByPath(currentBreakup, lineitem.id)} priceClass={lineitem.bold ? "text-black" : "label-14"} labelClass={lineitem.bold ? "text-black heading-16" : "text-black label-16"} bold={lineitem.bold} isLoading={isLoading} />
    )
  ))
);

const BreakupDetails = ({ priceBreakup }) => {
  const [isGstView, setIsGstView] = useState(true);

  const { activeRequests } = useSelector((state) => state.app);
  const isLoading = activeRequests?.includes('get-org-price-breakup');

  return (
    <div className="bill-container label-16">
      <div className="flex items-center gap-3">
        <Select
          label="With GST"
          width="100%"
          className="flex-1"
          onClick={() => setIsGstView(true)}
          selected={isGstView}
        />
        <Select
          label="Without GST"
          width="100%"
          className="flex-1"
          onClick={() => setIsGstView(false)}
          selected={!isGstView}
        />
      </div>
      <div className="organizer-breakup flex flex-col gap-4 text-grey-70">
        <Breakup
          config={isGstView ? GST_TICKET_BREAKUP : NONGST_TICKET_BREAKUP}
          isLoading={isLoading}
          currentBreakup={priceBreakup || {}}
        />
        <div className="horizontal-divider" />
        <Breakup
          config={isGstView ? GST_ORGANIZER_BREAKUP : NONGST_ORGANIZER_BREAKUP}
          isLoading={isLoading}
          currentBreakup={priceBreakup || {}}
        />
      </div>
      <div className="flex w-full bg-light p-3 px-4 rounded-3xl justify-between my-2 items-center">
        <div className="flex gap-3">
          <div className="bg-white rounded-full p-1 self-center">
            <Sparkle size={24}
              color="#FF2459"
              weight="fill" />
          </div>
          <span className="heading-16 mt-1.5">Money to you</span>
        </div>
        <span className="heading-16 text-primary">
          {' '}
          ₹ {(priceBreakup?.Organizer?.[isGstView ? 'GST' : 'NON-GST']?.receivable_amount || 0)?.toFixed(2)}{' '}
        </span>
      </div>

      <div className="flex w-full bg-grey-5 px-6 py-5 rounded-xl gap-5 items-center">
        <Info color="#101010" size={24} weight="fill" className='shrink-0' />
        <div className="text-16">You're just viewing the breakup here. You'll choose GST or non-GST in the final step.</div>
      </div>
    </div>
  );
};

export default BreakupDetails;
